/**
 * Global search modal (patch D4).
 *
 * Opens via the topbar search affordance (click or ⌘K / Ctrl-K). Shows
 * a text input + three result buckets (projects, items, suppliers) with
 * keyboard navigation (↑/↓ to highlight, Enter to navigate, Esc to
 * close). The query is debounced ~200ms — typing fast doesn't issue a
 * request per keystroke.
 *
 * Why not a full Combobox library: the result count is bounded
 * (PER_BUCKET_LIMIT × 3 = 24 max), so we can hand-roll the keyboard
 * interactions without bringing in another dep. The modal is its own
 * focus trap because the underlying `Modal` already handles Esc + scroll
 * lock.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Briefcase, Building2, FileText, Hash, MessageSquare, Package2, Search } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { ProjectNumber } from '@/components/ui/project-number';
import { formatDate } from '@procurement/shared';
import { Spinner } from '@/components/ui/spinner';
import {
  flattenSearchResults,
  searchResultHref,
  useGlobalSearch,
  type AnySearchResult,
  type CommunicationSearchResult,
  type ItemSearchResult,
  type ProjectSearchResult,
  type SupplierSearchResult,
  type TagSearchResult,
} from '@/lib/search-queries';

interface Props {
  open: boolean;
  onClose: () => void;
}

const DEBOUNCE_MS = 200;

export function GlobalSearchModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when reopened
  useEffect(() => {
    if (open) {
      setInput('');
      setDebouncedQuery('');
      // Give the modal a tick to mount and the input to exist
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Debounce input → debouncedQuery
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(input), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input]);

  const search = useGlobalSearch(debouncedQuery);
  const flat = useMemo(() => flattenSearchResults(search.data), [search.data]);

  // Keyboard navigation — track active index over the flat list
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    setActiveIndex(0);
  }, [flat]);

  const activate = useCallback(
    (r: AnySearchResult) => {
      onClose();
      navigate({ to: searchResultHref(r) });
    },
    [navigate, onClose],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flat.length === 0) return;
      setActiveIndex((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flat.length === 0) return;
      setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = flat[activeIndex];
      if (target) activate(target);
    }
  };

  const hasQuery = debouncedQuery.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} size="lg" title="Search">
      <div onKeyDown={handleKeyDown}>
        <div className="-mt-2 flex items-center gap-2 border-b border-border-default pb-3">
          <Search className="h-4 w-4 text-tertiary" strokeWidth={1.5} />
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search projects, parts, suppliers…"
            className="flex-1 bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
          />
          {search.isFetching ? <Spinner className="h-4 w-4 text-tertiary" /> : null}
        </div>

        <div className="mt-3 max-h-[60vh] overflow-y-auto">
          {!hasQuery ? (
            <PlaceholderHint />
          ) : search.error ? (
            <p className="py-6 text-center text-sm text-danger-fg">
              Search failed. {search.error.message}
            </p>
          ) : flat.length === 0 && !search.isFetching ? (
            <p className="py-6 text-center text-sm text-tertiary">
              No matches for &ldquo;{debouncedQuery}&rdquo;.
            </p>
          ) : (
            <ResultGroups results={search.data} flat={flat} activeIndex={activeIndex} onActivate={activate} />
          )}
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Placeholder shown before the user types
// ---------------------------------------------------------------------------

function PlaceholderHint() {
  return (
    <div className="py-8 text-center text-sm text-tertiary">
      Start typing to search across projects, parts, suppliers, tags, and communications.
      <div className="mt-3 inline-flex items-center gap-2 text-xs">
        <Hotkey>↑</Hotkey>
        <Hotkey>↓</Hotkey>
        <span>navigate</span>
        <Hotkey>↵</Hotkey>
        <span>open</span>
        <Hotkey>esc</Hotkey>
        <span>close</span>
      </div>
    </div>
  );
}

function Hotkey({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded-sm border border-border-default bg-surface px-1.5 py-0.5 text-[10px] font-medium text-secondary">
      {children}
    </kbd>
  );
}

// ---------------------------------------------------------------------------
// Result grouping
// ---------------------------------------------------------------------------

interface ResultGroupsProps {
  results: ReturnType<typeof useGlobalSearch>['data'];
  flat: AnySearchResult[];
  activeIndex: number;
  onActivate: (r: AnySearchResult) => void;
}

function ResultGroups({ results, flat, activeIndex, onActivate }: ResultGroupsProps) {
  if (!results) return null;
  return (
    <div className="space-y-3">
      {results.projects.length > 0 ? (
        <Section title="Projects" count={results.projects.length}>
          {results.projects.map((r) => {
            const idx = flat.indexOf(r);
            return (
              <ProjectRow
                key={r.id}
                result={r}
                active={idx === activeIndex}
                onClick={() => onActivate(r)}
              />
            );
          })}
        </Section>
      ) : null}

      {results.items.length > 0 ? (
        <Section title="Parts &amp; sub-parts" count={results.items.length}>
          {results.items.map((r) => {
            const idx = flat.indexOf(r);
            return (
              <ItemRow
                key={r.id}
                result={r}
                active={idx === activeIndex}
                onClick={() => onActivate(r)}
              />
            );
          })}
        </Section>
      ) : null}

      {results.suppliers.length > 0 ? (
        <Section title="Suppliers" count={results.suppliers.length}>
          {results.suppliers.map((r) => {
            const idx = flat.indexOf(r);
            return (
              <SupplierRow
                key={r.id}
                result={r}
                active={idx === activeIndex}
                onClick={() => onActivate(r)}
              />
            );
          })}
        </Section>
      ) : null}

      {results.tags.length > 0 ? (
        <Section title="Tags" count={results.tags.length}>
          {results.tags.map((r) => {
            const idx = flat.indexOf(r);
            return (
              <TagRow
                key={r.name}
                result={r}
                active={idx === activeIndex}
                onClick={() => onActivate(r)}
              />
            );
          })}
        </Section>
      ) : null}

      {results.communications.length > 0 ? (
        <Section title="Communications" count={results.communications.length}>
          {results.communications.map((r) => {
            const idx = flat.indexOf(r);
            return (
              <CommunicationRow
                key={r.id}
                result={r}
                active={idx === activeIndex}
                onClick={() => onActivate(r)}
              />
            );
          })}
        </Section>
      ) : null}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: React.ReactNode;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-tertiary">
        <span>{title}</span>
        <span>{count}</span>
      </div>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row variants
// ---------------------------------------------------------------------------

interface RowShellProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
}

function RowShell({ active, onClick, icon, title, subtitle, trailing }: RowShellProps) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors ${
          active ? 'bg-subtle' : 'hover:bg-subtle'
        }`}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-base text-secondary">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm text-primary">{title}</div>
          {subtitle ? <div className="truncate text-xs text-secondary">{subtitle}</div> : null}
        </div>
        {trailing ? <div className="shrink-0 text-xs text-secondary">{trailing}</div> : null}
      </button>
    </li>
  );
}

function ProjectRow({
  result,
  active,
  onClick,
}: {
  result: ProjectSearchResult;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <RowShell
      active={active}
      onClick={onClick}
      icon={<Briefcase className="h-4 w-4" strokeWidth={1.5} />}
      title={
        <>
          <ProjectNumber number={result.projectNumber} size="sm" />{' '}
          <span>{result.projectTitle}</span>
        </>
      }
      trailing={result.lifecycleState === 'CLOSED' ? 'Closed' : null}
    />
  );
}

function ItemRow({
  result,
  active,
  onClick,
}: {
  result: ItemSearchResult;
  active: boolean;
  onClick: () => void;
}) {
  const Icon = result.itemType === 'PART' ? Package2 : FileText;
  return (
    <RowShell
      active={active}
      onClick={onClick}
      icon={<Icon className="h-4 w-4" strokeWidth={1.5} />}
      title={result.name}
      subtitle={
        <>
          {result.parentItemName ? <span>↳ {result.parentItemName} · </span> : null}
          <ProjectNumber number={result.projectNumber} size="sm" /> — {result.projectTitle}
        </>
      }
    />
  );
}

function SupplierRow({
  result,
  active,
  onClick,
}: {
  result: SupplierSearchResult;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <RowShell
      active={active}
      onClick={onClick}
      icon={<Building2 className="h-4 w-4" strokeWidth={1.5} />}
      title={result.name}
      trailing={!result.isActive ? 'Inactive' : null}
    />
  );
}

function TagRow({
  result,
  active,
  onClick,
}: {
  result: TagSearchResult;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <RowShell
      active={active}
      onClick={onClick}
      icon={<Hash className="h-4 w-4" strokeWidth={1.5} />}
      title={
        <span className="font-mono text-sm text-accent-strong">#{result.name}</span>
      }
      subtitle={`${result.usageCount} attachment${result.usageCount === 1 ? '' : 's'}`}
    />
  );
}

function CommunicationRow({
  result,
  active,
  onClick,
}: {
  result: CommunicationSearchResult;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <RowShell
      active={active}
      onClick={onClick}
      icon={<MessageSquare className="h-4 w-4" strokeWidth={1.5} />}
      title={result.subject}
      subtitle={
        <>
          {result.projectNumber ? (
            <ProjectNumber number={result.projectNumber} size="sm" />
          ) : (
            <span className="italic">Master-anchored</span>
          )}
          {result.occurredAt ? (
            <span> · {formatDate(result.occurredAt)}</span>
          ) : null}
        </>
      }
    />
  );
}
